import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { modalEmployeePending, permissionsAtom, userTypeAtom } from "../../atoms/atom";
import { EmployeeService } from "../../services/EmployeeService";
import { CreatedEmployeeDTO } from "../../types/CreatedEmployeeDTO";
import { EmployeeDTO } from "../../types/EmployeeDTO";
import { PositionDTO } from "../../types/PositionDTO";
import { useUtils } from "../../utils/utilFunctions";
import { useAlert } from "../contextProviders/AlertProvider";
import "./EmployeeEditModal.scss";

const EmployeeEditModal: FC<{props: CreatedEmployeeDTO}> = ({props: employee}) => {
	const [open, setOpen] = useAtom(modalEmployeePending);
	const [userType] = useAtom(userTypeAtom);
	const [positions, setPositions] = useState<PositionDTO[]>([]);
	const [position, setPosition] = useState('');
	const [permissions] = useAtom(permissionsAtom);
	const [loading, setLoading] = useState(false);
	const service = new EmployeeService();
	const { showError, showAlert } = useAlert();
	const { formatDate, verifyPermission } = useUtils();

	const externalUser = "EXTERNO" === userType;

	useEffect(() => {
		setPosition('');
		if (!employee.sectorId || !open || !employee.pending) return;
		const loadPositions = async () => {
			const response = await service.getPositionsBySector(employee.sectorId);
			if (response && response.data) {
				setPositions(response.data);
			}
		};
		loadPositions();
		if (!!permissions.length) {
			verifyPermission(permissions, 'EMPLOYEES');
		}
	}, [open]);

	return (
		<Dialog
			open={open}
			onClose={() => setOpen(false)}
			id="dialog-edit-employee"
		>
			<DialogTitle>{employee.pending ? "Verificar funcionário pendente" : "Funcionário cadastrado"}</DialogTitle>
			<IconButton
				aria-label="close"
				onClick={() => setOpen(false)}
				sx={(theme) => ({
					position: 'absolute',
					right: 8,
					top: 8,
					color: theme.palette.grey[500],
				})}
			>
				<CloseIcon />
			</IconButton>
			<DialogContent className="employee-edit">
				<Box className="table-box">
					<Table>
						<TableHead>
							<TableRow>
								<TableCell className="cell-pending bold title" colSpan={2} align="center">Dados do funcionário</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							<TableRow>
								<TableCell className="cell-pending bold">Empresa:</TableCell>
								<TableCell className="cell-pending weak">{employee.companyName}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell className="cell-pending bold">CNPJ:</TableCell>
								<TableCell className="cell-pending weak">{employee.cnpj}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell className="cell-pending bold">Nome:</TableCell>
								<TableCell className="cell-pending weak">{employee.name}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell className="cell-pending bold">CPF:</TableCell>
								<TableCell className="cell-pending weak">{employee.cpf}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell className="cell-pending bold">Setor:</TableCell>
								<TableCell className="cell-pending weak">{employee.sector}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell className="cell-pending bold">Cargo{employee.pending ? ' sugerido' : ''}:</TableCell>
								<TableCell className="cell-pending weak">{employee.position}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell className="cell-pending bold">Sexo:</TableCell>
								<TableCell className="cell-pending weak">{employee.sex === "M" ? 'Masculino' : 'Feminino'}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell className="cell-pending bold">Data de nascimento:</TableCell>
								<TableCell className="cell-pending weak">{formatDate(new Date(employee.birthDate)) }</TableCell>
							</TableRow>
							<TableRow sx={{display: employee.admissionDate ? '' : 'none'}}>
								<TableCell className="cell-pending bold">Data de admissão:</TableCell>
								<TableCell className="cell-pending weak">{formatDate(new Date(employee.admissionDate)) }</TableCell>
							</TableRow>
							<TableRow>
								<TableCell className="cell-pending bold">RG:</TableCell>
								<TableCell className="cell-pending weak">{employee.rg}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell className="cell-pending bold">NIT:</TableCell>
								<TableCell className="cell-pending weak">{employee.nit}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell className="cell-pending bold">CTPS:</TableCell>
								<TableCell className="cell-pending weak">{employee.ctps}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell className="cell-pending bold">Matrícula:</TableCell>
								<TableCell className="cell-pending weak">{employee.esocial}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell className="cell-pending bold">Categoria:</TableCell>
								<TableCell className="cell-pending weak">{employee.category}</TableCell>
							</TableRow>
							<TableRow sx={{display: employee.pending ? '' : 'none'}}>
								<TableCell className="cell-pending bold" sx={{ color: '#fbff00ff' }}>Escolher cargo existente:</TableCell>
								<TableCell className="cell-pending weak" sx={{padding: "5px"}}>
									<FormControl sx={{width: "100%"}}>
										<Select
											id="select-existing-position"
											value={position}
											onChange={(e) => setPosition(e.target.value)}
											size='medium'
										>
											{positions.map((item) => (
												<MenuItem
													key={item.id_cargo}
													value={item.id_cargo}
												>
													{`${item.cargo} - ${item.funcao} - ${item.CBO}`}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell className="cell-pending bold">Função:</TableCell>
								<TableCell className="cell-pending weak">{employee.functionName}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell className="cell-pending bold">Criado por:</TableCell>
								<TableCell className="cell-pending weak">{`${employee.createdBy} em ${employee.createdAt}`}</TableCell>
							</TableRow>
							<TableRow sx={{display: employee.approvedBy ? '' : 'none'}}>
								<TableCell className="cell-pending bold">Aprovado por:</TableCell>
								<TableCell className="cell-pending weak">{`${employee.approvedBy} em ${employee.approvedAt}`}</TableCell>
							</TableRow>
							<TableRow sx={{display: employee.pending || externalUser ? 'none' : ''}}>
								<TableCell className="cell-pending bold">Ver no SGG</TableCell>
								<TableCell className="cell-pending weak">
									<a
										style={{color: "#a6a0d9ff"}}
										href={`https://app.sgg.net.br/index.php?func=funcionario&origem=pesquisa_visual&id_funcionario=${employee.sggId}`}
										target="_blank" rel="noopener noreferrer"
									>
										ACESSAR CADASTRO NO SGG
									</a>
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</Box>
			</DialogContent>
			{employee.pending
				? <DialogActions sx={{paddingRight: '2vh'}}>
					<Button
						variant="contained"
						color="success"
						disabled={!position}
						onClick={() => createEmployee()}
					>
						{loading
							? <CircularProgress sx={{color: "#292929ff"}} size={'25px'}/>
							: "Cadastrar"
						}
					</Button>
				</DialogActions>
				: <></>
			}
		</Dialog>
	);

	async function createEmployee() {
		setLoading(true);
		try {
			const data = {
				cargo: positions.find(i => i.id_cargo === position)?.cargo,
				id_cargo: position
			} as EmployeeDTO;
			const response = await service.approveEmployee(employee.id, data);
			if (response && 200 === response.status && !response.data) {
				showAlert('Funcionário criado com sucesso.');
			}
			else if (response && !!response.data) {
				showError(response.data);
			}
		} catch (error) {
			showError();
		} finally {
			setLoading(false);
			setOpen(false);
		}
	}
}

export default EmployeeEditModal;